import { Button, Modal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { StaffMember } from "@/lib/api/types";
import { captainFirstName } from "@/modules/pos/lib/captain";

/**
 * D153 — the «الكابتن» chip row: the store's waiters, in the server's order
 * (most recently captained first, then by name), single-select.
 *
 * The SAME inline-chip pattern the covers chips use (D45): nothing selected
 * unless the default applies, one tap to answer, the selected chip tapped
 * again clears it, and skipping costs zero taps. With `noneChip` a
 * «من غير كابتن» chip is offered too — the bill-head sheet, where clearing
 * has to be an explicit choice. Names are first names, the floor's own.
 */
export function CaptainChips({
  chips,
  selected,
  onPick,
  disabled,
  noneChip = false,
}: {
  chips: readonly StaffMember[];
  selected: string | null;
  /** The chip tapped — `null` is the «من غير كابتن» chip. */
  onPick: (sellerId: string | null) => void;
  disabled?: boolean;
  noneChip?: boolean;
}) {
  const t = useTranslate();
  const chipClass = (isSelected: boolean) =>
    [
      "flex h-9 min-w-[40px] items-center justify-center rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2.5 text-[length:var(--font-size-sm)] font-extrabold transition-colors",
      isSelected
        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
    ].join(" ");

  if (chips.length === 0) {
    return (
      <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
        {t((m) => m.captain.noWaiters)}
      </p>
    );
  }
  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label={t((m) => m.captain.label)}
    >
      {noneChip ? (
        <button
          type="button"
          aria-pressed={selected === null}
          disabled={disabled}
          data-testid="captain-chip-none"
          onClick={() => onPick(null)}
          className={chipClass(selected === null)}
        >
          {t((m) => m.captain.none)}
        </button>
      ) : null}
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          aria-pressed={selected === chip.id}
          // The whole name for a screen reader; the first name on the chip.
          aria-label={chip.name}
          title={chip.name}
          disabled={disabled}
          data-testid={`captain-chip-${chip.id}`}
          onClick={() => onPick(chip.id)}
          className={chipClass(selected === chip.id)}
        >
          <bdi>{captainFirstName(chip.name)}</bdi>
        </button>
      ))}
    </div>
  );
}

/**
 * D153 — changing the captain of an OPEN dine-in bill, from its header.
 *
 * Offered only to an `INVOICE_CLOSE` holder (cashier, manager, owner), which
 * is the rung `setCaptain` checks. One tap on a chip IS the change — the page
 * sends `setCaptain` and closes the sheet; tapping the current captain does
 * nothing. The hint says the change is logged, because it is: that log is
 * what makes moving a name off a table visible.
 */
export function CaptainSheet({
  chips,
  current,
  isSubmitting,
  onCancel,
  onPick,
}: {
  chips: readonly StaffMember[];
  current: string | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onPick: (sellerId: string | null) => void;
}) {
  const t = useTranslate();
  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.captain.label)}
      closeLabel={t((m) => m.common.close)}
      data-testid="captain-sheet"
      footerActions={
        <Button variant="outline" size="touch" onClick={onCancel}>
          {t((m) => m.common.cancel)}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <CaptainChips
          chips={chips}
          selected={current}
          noneChip
          disabled={isSubmitting}
          onPick={(sellerId) => {
            if (sellerId === current) return;
            onPick(sellerId);
          }}
        />
        <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
          {t((m) => m.captain.sheetHint)}
        </p>
      </div>
    </Modal>
  );
}
